import { get, writable, derived } from 'svelte/store';

/** @typedef {import('./apiTypes.js').ApiResponse} ApiResponse */

/**
 * @typedef {{
 *   uuid: string;
 *   salutation?: string;
 *   first_name?: string;
 *   last_name?: string;
 *   email?: string;
 *   phone?: string;
 *   user_role?: { name: string };
 *   media_file?: { url: string };
 *   active_team_uuid?: string;
 *   profile_image_url?: string;
 *   [key: string]: unknown;
 * }} AuthUser
 */

/**
 * @typedef {{
 *   token: string | null;
 *   user: AuthUser | null;
 *   success: boolean;
 *   message: string;
 *   userInitials?: string | null;
 * }} AuthState
 */

// Load initial state from localStorage or use default values
const storedState = localStorage.getItem('authState');
const loadedState = storedState ? JSON.parse(storedState) : null;
const initialState = loadedState || {
    token: null,
    user: {
        uuid: '',
        salutation: '', 
        first_name: '', 
        last_name: '', 
        email: '', 
        phone: '' 
    },
    success: false,
    message: '',
};

const { subscribe, set, update } = writable(initialState);

// Subscribe to changes in the state and save them to localStorage
subscribe(currentState => {
    localStorage.setItem('authState', JSON.stringify(currentState));
});

export const auth = {
    subscribe,
    update,
    // Normalize domain: handle undefined, empty string, or literal "undefined"/""
    domain: (() => {
        const envUrl = import.meta.env.VITE_API_URL;
        if (!envUrl || envUrl === 'undefined' || envUrl === '""' || envUrl === '') {
            return '';
        }
        return envUrl;
    })(),

    /**
     * Make a request to the server.
     * @param {string} method - The HTTP method to use.
     * @param {string} url - The URL to request.
     * @param {Record<string, unknown> | null} [body] - The body of the request.
     * @returns {Promise<ApiResponse>} The response from the server.
     */
    async makeRequest(method, url, body = null) {
        const token = localStorage.getItem('token');

        const options = {
            method: method,
            headers: {
                'Content-Type': 'application/json',
                ...(token && { 'Authorization': `Bearer ${token}` })
            },
            body: body ? JSON.stringify(body) : null
        };
        try {
            const response = await fetch(this.domain + url, options);
            const contentType = response.headers.get('content-type');
            
            // Check if response is JSON
            if (contentType && contentType.includes('application/json')) {
                const responseBody = await response.json();
                
                if (responseBody) {
                    update(state => {
                        state.message = responseBody.message;
                        state.success = responseBody.success;
                        return state;
                    });
                }
                // Return response body even if status is not OK (to handle error responses with data)
                return responseBody;
            } else {
                // Non-JSON response (HTML error page, etc.)
                const text = await response.text();
                throw new Error(`Server returned HTML instead of JSON: ${text.substring(0, 100)}...`);
            }
        } catch (error) {
            const errMessage = error instanceof Error ? error.message : String(error);
            console.error('Error:', error);
            update(state => {
                state.message = errMessage;
                state.success = false;
                return state;
            });
            return { success: false, message: errMessage };
        }            
    },

    /**
     * Log in a user.
     * @param {string} email - The user's email.
     * @param {string} password - The user's password.
     * @returns {Promise<boolean>} Whether the login was successful.
     */
    async login(email, password) {
        const body = await this.makeRequest('POST', '/api/v1/user/login', { email, password });
        if (body.success) {
            localStorage.setItem('token', body.data.token);
            update(state => {
                state.token = body.data.token;
                state.user = body.data.user;
                return state;
            });
            return true;
        }
        return false;
    },

    /**
     * Log out the current user.
     * @returns {Promise<void>}
     */
    async logout() {
        const body = await this.makeRequest('GET', '/api/v1/user/logout');
        localStorage.removeItem('token');
        update(state => {
            state.token = null;
            state.user = null;
            state.userInitials = null;
            return state;
        });
    },

    /**
     * Sign up a new user.
     * @param {string} email - The user's email.
     * @returns {Promise<boolean>} Whether the signup was successful.
     */
    async signup(email) {
        const body = await this.makeRequest('POST', '/api/v1/user/signup', {
            email,
        });
        return body.success;
    },

    /**
     * Complete onboarding: save profile, create team, assign plan with trial.
     * @param {Record<string, unknown>} data - { first_name, last_name, mobile, team_name, payment_plan_uuid }
     * @returns {Promise<ApiResponse>} The response from the server.
     */
    async completeOnboarding(data) {
        const currentState = get(auth);
        const body = await this.makeRequest('POST', `/api/v1/user/${currentState.user.uuid}/complete-onboarding`, data);
        if (body.success && body.data) {
            update(state => {
                state.user = { ...state.user, ...body.data };
                return state;
            });
        }
        return body;
    },

    /**
     * Update a user's password.
     * @param {string} uuid - The user's UUID.
     * @param {string} password - The new password.
     * @param {string} c_password - The confirmation of the new password.
     * @returns {Promise<ApiResponse>} The response from the server.
     */
    async updatePassword(uuid, password, c_password) {
        const body = await this.makeRequest('PUT', '/api/v1/user/update-password/' + uuid, { password, c_password });
        return body;
    },

    /**
     * Send a password reset email.
     * @param {string} email - The user's email.
     * @returns {Promise<ApiResponse>} The response from the server.
     */
    async resetPasswordSend(email) {
        const body = await this.makeRequest('GET', '/api/v1/user/update-password-email/' + email);
        return body;
    },

    /**
     * Update a user's details.
     * @param {Record<string, unknown>} user - The new details for the user.
     * @returns {Promise<boolean>} Whether the user update was successful.
     */
    async userUpdate(user) {
        const currentState = get(auth);
        const body = await this.makeRequest('PUT', '/api/v1/user/' + currentState.user.uuid, user);
        update(state => {
            state.user = body.data;
            return state;
        });
 
        return body.success;
    },

    /**
     * Refresh user details from the server.
     * @returns {Promise<boolean>} Whether the user update was successful.
     */
    async userRefresh() {
        const currentState = get(auth);
        const body = await this.makeRequest('GET', '/api/v1/user/' + currentState.user.uuid);
        update(state => {
            state.user = body.data;
            return state;
        });
 
        return body.success;
    },

    /**
     * Get a user from the server.
     * @param {string} token - The user's token.
     * @returns {Promise<Object>} The user.
     */
    async getUserByToken(token) {
        const body = await this.makeRequest('GET', '/api/v1/user/token/' + token);
        return body;
    },

    /**
     * Check if the current user is an admin or owner.
     * @returns {boolean} Whether the user is an admin or owner.
     */
    userIsAdmin() {
        const currentState = get(auth);
        if (!currentState.user) {
            return false;
        }
        return currentState.user.user_role && 
               (currentState.user.user_role.name === 'Admin' || 
               currentState.user.user_role.name === 'Owner');
    },    
};

/**
 * Get the initials of the current user.
 * @type {import('svelte/store').Readable<string>}
 */
export const userInitials = derived(auth, $auth => {
    if ($auth.user && $auth.user.first_name && $auth.user.last_name) {
        return ($auth.user.first_name[0] + $auth.user.last_name[0]).toUpperCase();
    } else if ($auth.user && $auth.user.email) {
        return $auth.user.email[0].toUpperCase();
    } else {
        return '';
    }
});

/**
 * Get the image of the current user.
 * @type {import('svelte/store').Readable<string>}
 */
export const userProfileImageUrl = derived(auth, $auth => {
    // Ensure $auth.user and $auth.user.media_file are defined before accessing url
    if ($auth.user && $auth.user.media_file && $auth.user.media_file.url) {
        return $auth.user.media_file.url;
    }
    // Return an empty string for consistency if there's no URL
    return '';
});